import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Badge,
  Card,
  Heading,
  Separator,
  Alert,
  Spinner,
  Input,
  Textarea,
  Checkbox,
  IconButton,
} from '@chakra-ui/react';
import { Tabs } from '@chakra-ui/react';
import { Collapsible } from '@chakra-ui/react';
import { toaster } from '../../components/ui/toaster';
import { FiSearch, FiCheck, FiPlus, FiX, FiChevronDown, FiChevronRight, FiInfo } from 'react-icons/fi';
import { SystemStandard } from '../../types';
import type { ReviewTemplate } from '../../types';
import { systemStandardsAPI, templatesAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { getAssessmentUnitLabels } from '../../utils/frameworkTerminology';

const { Body: CardBody } = Card;

interface Section {
  section_id: string;
  section_title: string;
  section_content: string;
  section_path: string;
  is_selected: boolean;
}

interface TemplateSelectionStepProps {
  selectedStandard: SystemStandard | null;
  selectedTemplate: ReviewTemplate | null;
  selectedSections: string[];
  selectedSubsections?: {[key: string]: string[]};
  customSections: string[];
  onTemplateSelect: (template: ReviewTemplate | null) => void;
  onSectionsSelect: (sections: string[]) => void;
  onSubsectionsSelect?: (subsections: {[key: string]: string[]}) => void;
  onCustomSectionsUpdate: (sections: string[]) => void;
}

const TemplateSelectionStep: React.FC<TemplateSelectionStepProps> = ({
  selectedStandard,
  selectedTemplate,
  selectedSections,
  selectedSubsections: propSelectedSubsections,
  customSections,
  onTemplateSelect,
  onSectionsSelect,
  onSubsectionsSelect,
  onCustomSectionsUpdate,
}) => {
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [newCustomSection, setNewCustomSection] = useState('');
  const { t, i18n } = useTranslation();
  const assessmentTerminology = useMemo(
    () => getAssessmentUnitLabels(selectedStandard?.standard_type, selectedSections, i18n.language),
    [selectedStandard?.standard_type, selectedSections, i18n.language]
  );

  useEffect(() => {
    if (selectedStandard) {
      loadTemplatesAndSections();
    }
  }, [selectedStandard]);

  const loadTemplatesAndSections = async () => {
    setIsLoading(true);
    try {
      // Fetch real templates from backend
      let fetchedTemplates: ReviewTemplate[] = [];
      if (selectedStandard?.standard_type) {
        fetchedTemplates = await templatesAPI.getAll(selectedStandard.standard_type);
      } else {
        fetchedTemplates = await templatesAPI.getAll();
      }
      setTemplates(fetchedTemplates);
      // Load real sections from API
      if (selectedStandard) {
        const sectionsResponse = await systemStandardsAPI.getSections(selectedStandard.id.toString());
        setSections(sectionsResponse.sections || []);
      }
    } catch (error) {
      console.error('Error loading templates or sections:', error);
      toaster.create({
        title: t('createReview.steps.errorLoadingData'),
        description: t('createReview.steps.failedToLoadTemplatesOrSectionsTryAgain'),
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: ReviewTemplate | null) => {
    onTemplateSelect(template);
    if (template) {
      if (template.sections.includes('all')) {
        // Select all sections
        const allSectionIds = sections.map(s => s.section_id);
        onSectionsSelect(allSectionIds);
      } else {
        onSectionsSelect(template.sections);
      }
      toaster.create({
        title: t('createReview.steps.templateSelected'),
        description: t('createReview.steps.templateSelectedDescription', { name: template.name }),
        type: 'success',
      });
    }
  };

  // Find the full section object by ID
  const findSection = (sectionId: string) => sections.find(s => s.section_id === sectionId);

  const handleSectionToggle = (sectionId: string) => {
    const section = findSection(sectionId);
    const subsections = section ? parseSubsections(section) : [];
    const hasSubsections = subsections.length > 0;
    const currentTokens = getSectionSelectionTokens(sectionId);

    if (hasSubsections) {
      const allTokens = subsections.map((sub: any) => getSubsectionToken(sub.id));
      const allSelected = allTokens.length > 0 && allTokens.every((token: string) => currentTokens.includes(token));

      if (allSelected) {
        commitSectionSelections(sectionId, []);
      } else {
        commitSectionSelections(sectionId, allTokens);
      }
    } else {
      // For sections WITHOUT subsections: simple toggle
      const isCurrentlySelected = selectedSections.includes(sectionId);
      if (isCurrentlySelected) {
        commitSectionSelections(sectionId, []);
      } else {
        commitSectionSelections(sectionId, [sectionId]);
      }
    }
  };

  const handleSelectAllSections = () => {
    const allSectionIds = sections.map(s => s.section_id);
    onSectionsSelect(allSectionIds);

    // Also select all subsections for every section that has them
    const newSelectedSubsections: {[key: string]: string[]} = {};
    for (const section of sections) {
      const subsections = parseSubsections(section);
      if (subsections.length > 0) {
        newSelectedSubsections[section.section_id] = subsections.map((sub: any) => getSubsectionToken(sub.id));
      }
    }
    setSelectedSubsections(newSelectedSubsections);
    if (onSubsectionsSelect) {
      onSubsectionsSelect(newSelectedSubsections);
    }

    toaster.create({
      title: t('createReview.steps.allSectionsSelected', { unitPlural: assessmentTerminology.plural }),
      description: t('createReview.steps.allSectionsSelectedDescription', { unitPlural: assessmentTerminology.plural }),
      type: 'success',
    });
  };

  const handleDeselectAllSections = () => {
    onSectionsSelect([]);
    setSelectedSubsections({});
    if (onSubsectionsSelect) {
      onSubsectionsSelect({});
    }
    toaster.create({
      title: t('createReview.steps.allSectionsDeselected', { unitPlural: assessmentTerminology.plural }),
      description: t('createReview.steps.allSectionsDeselectedDescription', { unitPlural: assessmentTerminology.plural }),
      type: 'info',
    });
  };

  const handleAddCustomSection = () => {
    if (newCustomSection.trim()) {
      const updatedSections = [...customSections, newCustomSection.trim()];
      onCustomSectionsUpdate(updatedSections);
      setNewCustomSection('');
      toaster.create({
        title: t('createReview.steps.customSectionAdded', { unitSingular: assessmentTerminology.singular }),
        description: t('createReview.steps.customSectionAddedDescription', { unitSingular: assessmentTerminology.singular }),
        type: 'success',
      });
    }
  };

  const handleRemoveCustomSection = (index: number) => {
    const updatedSections = customSections.filter((_, i) => i !== index);
    onCustomSectionsUpdate(updatedSections);
  };

  // Parse subsections from section content or use explicit subsections
  const parseSubsections = (section: any) => {
    // First check if the section has explicit subsections
    if (section.subsections && Array.isArray(section.subsections)) {
      return section.subsections.map((subsection: any) => ({
        id: subsection.subsection_id,
        title: subsection.subsection_title,
        content: subsection.subsection_content || subsection.subsection_title,
        fullText: `${subsection.subsection_id} ${subsection.subsection_title}`,
        sub_subsections: subsection.sub_subsections || null
      }));
    }

    // Otherwise, parse from section_content - but ONLY for sections with control-number patterns
    // (e.g., Annex A "A.5.1 ..., A.5.2 ..." or NIST "PR.AC ..., PR.AT ...")
    // Do NOT comma-split plain descriptive text from ISO body clauses
    const sectionContent = section.section_content || '';
    if (!sectionContent || sectionContent.length < 10) {
      return []; // Too short to have meaningful subsections
    }

    // Only attempt comma-splitting if content contains control-number patterns
    const hasControlNumbers = /[A-Z]+\.\d+(?:\.\d+)?\s+\w/.test(sectionContent);
    if (!hasControlNumbers) {
      return []; // Plain descriptive text - no subsections to parse
    }

    // Enhanced parsing to preserve control numbers
    const subsections = sectionContent
      .split(/[,;]/)
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 3) // Filter out very short items
      .map((item: string) => {
        // Extract control number if present (e.g., "A.9.1 Business requirements")
        const controlMatch = item.match(/^([A-Z]+\.\d+(?:\.\d+)?)\s+(.+)/) || 
                           item.match(/^([A-Z]+\.[A-Z]+)\s+(.+)/); // For NIST format like PR.AC
        
        if (controlMatch) {
          return {
            id: controlMatch[1],
            title: controlMatch[2],
            content: item,
            fullText: item
          };
        } else {
          return null; // Skip items without control numbers
        }
      })
      .filter((item: any) => item !== null && item.title && item.title.length > 3); // Filter nulls and short items
    
    // Only return subsections if we have multiple meaningful items
    return subsections.length > 1 && subsections.length <= 15 ? subsections : [];
  };

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedSubsections, setExpandedSubsections] = useState<Set<string>>(new Set());
  const [selectedSubsections, setSelectedSubsections] = useState<{[key: string]: string[]}>(propSelectedSubsections || {}); // Store selected subsections per section

  const normalizeSelectionToken = (token: string) => token.split(':', 1)[0].trim();
  const getSectionSelectionTokens = (sectionId: string) =>
    (selectedSubsections[sectionId] || [])
      .map(normalizeSelectionToken)
      .filter(Boolean);
  const getSubsectionToken = (subsectionId: string) => subsectionId;
  const getMeasureToken = (subsectionId: string, measureId: string) => `${subsectionId}>${measureId}`;
  const getLeafMeasureIds = (subsection: any) => {
    const subSubs = subsection.sub_subsections;
    if (Array.isArray(subSubs) && subSubs.length > 0) {
      return subSubs.map((subSub: any) => subSub.id).filter(Boolean);
    }
    return [subsection.id].filter(Boolean);
  };
  const countSelectedLeafMeasuresForSubsection = (sectionId: string, subsection: any) => {
    const tokens = getSectionSelectionTokens(sectionId);
    const leafIds = getLeafMeasureIds(subsection);
    if (tokens.includes(subsection.id)) {
      return leafIds.length;
    }
    return leafIds.filter((measureId: string) => tokens.includes(getMeasureToken(subsection.id, measureId))).length;
  };
  const countTotalLeafMeasuresForSection = (section: any) => {
    const subsections = parseSubsections(section);
    if (subsections.length === 0) {
      return 1;
    }
    return subsections.reduce((total: number, subsection: any) => total + getLeafMeasureIds(subsection).length, 0);
  };
  const countSelectedLeafMeasuresForSection = (sectionId: string, section: any) => {
    const subsections = parseSubsections(section);
    if (subsections.length === 0) {
      return selectedSections.includes(sectionId) ? 1 : 0;
    }
    return subsections.reduce((total: number, subsection: any) => total + countSelectedLeafMeasuresForSubsection(sectionId, subsection), 0);
  };
  const formatSectionHeading = (section: any) => {
    const sectionId = String(section?.section_id || '');
    if (sectionId.startsWith('goal-')) {
      const goalNo = sectionId.split('-')[1] || '';
      return `الغاية ${goalNo}`;
    }
    return sectionId;
  };
  const selectedRequirementCount = sections.reduce(
    (total, section) => total + countSelectedLeafMeasuresForSection(section.section_id, section),
    0
  );
  const isSubsectionFullySelected = (sectionId: string, subsection: any) => {
    const tokens = getSectionSelectionTokens(sectionId);
    const measureIds = getLeafMeasureIds(subsection);
    if (tokens.includes(subsection.id)) {
      return true;
    }
    const selectedMeasureTokens = measureIds
      .map((measureId: string) => getMeasureToken(subsection.id, measureId))
      .filter((token: string) => tokens.includes(token));
    return selectedMeasureTokens.length === measureIds.length && measureIds.length > 0;
  };

  const commitSectionSelections = (sectionId: string, nextTokens: string[]) => {
    const normalizedTokens = Array.from(
      new Set(nextTokens.map(normalizeSelectionToken).filter(Boolean))
    );

    setSelectedSubsections(prev => {
      const next = { ...prev };
      if (normalizedTokens.length > 0) {
        next[sectionId] = normalizedTokens;
      } else {
        delete next[sectionId];
      }
      if (onSubsectionsSelect) {
        onSubsectionsSelect(next);
      }
      return next;
    });

    if (normalizedTokens.length > 0) {
      if (!selectedSections.includes(sectionId)) {
        onSectionsSelect([...selectedSections, sectionId]);
      }
    } else {
      onSectionsSelect(selectedSections.filter(id => id !== sectionId));
    }
  };

  const toggleSectionExpansion = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleSubsectionExpansion = (subsectionId: string) => {
    const newExpanded = new Set(expandedSubsections);
    if (newExpanded.has(subsectionId)) {
      newExpanded.delete(subsectionId);
    } else {
      newExpanded.add(subsectionId);
    }
    setExpandedSubsections(newExpanded);
  };

  const handleSubsectionToggle = (sectionId: string, subsectionId: string, _subsectionTitle: string) => {
    const currentTokens = getSectionSelectionTokens(sectionId);
    const subsectionToken = getSubsectionToken(subsectionId);
    const isSelected = currentTokens.includes(subsectionToken);
    const nextTokens = isSelected
      ? currentTokens.filter(token => token !== subsectionToken && !token.startsWith(`${subsectionId}>`))
      : [...currentTokens.filter(token => !token.startsWith(`${subsectionId}>`) && token !== subsectionToken), subsectionToken];
    commitSectionSelections(sectionId, nextTokens);
  };

  const handleMeasureToggle = (
    sectionId: string,
    subsectionId: string,
    measureId: string,
    allMeasureIds: string[]
  ) => {
    const currentTokens = getSectionSelectionTokens(sectionId);
    const subsectionToken = getSubsectionToken(subsectionId);
    const measureToken = getMeasureToken(subsectionId, measureId);
    const allMeasureTokens = allMeasureIds.map(id => getMeasureToken(subsectionId, id));
    const subsectionSelected = currentTokens.includes(subsectionToken);
    const measureSelected = subsectionSelected || currentTokens.includes(measureToken);

    let nextTokens: string[];
    if (subsectionSelected) {
      // Split a fully-selected subsection into individual measure selections,
      // leaving the clicked measure unchecked.
      nextTokens = allMeasureTokens.filter(token => token !== measureToken);
    } else if (measureSelected) {
      nextTokens = currentTokens.filter(token => token !== measureToken);
    } else {
      nextTokens = [...currentTokens, measureToken];
    }

    const selectedMeasureTokens = allMeasureTokens.filter(token => nextTokens.includes(token));
    if (selectedMeasureTokens.length === allMeasureTokens.length && allMeasureTokens.length > 0) {
      nextTokens = nextTokens.filter(token => !allMeasureTokens.includes(token));
      nextTokens.push(subsectionToken);
    }

    commitSectionSelections(sectionId, nextTokens);
  };

  const filteredSections = sections.filter(section =>
    section.section_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.section_path.toLowerCase().includes(searchTerm.toLowerCase())
  );
  if (isLoading) {
    return (
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="md" mb={2}>
            {t('createReview.steps.selectAssessmentScopeTitle')}
          </Heading>
          <Text color="gray.600">
              {t('createReview.steps.selectAssessmentScopeLoadingSubtitle', {
                unitPlural: assessmentTerminology.plural,
              })}
            </Text>
        </Box>
        
        <VStack gap={4} py={12}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.600">{t('createReview.steps.loadingTemplatesAndSections')}</Text>
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack gap={6} align="stretch">
      <Box>
        <Heading size="md" mb={2}>
          {t('createReview.steps.selectAssessmentScopeTitle')}
        </Heading>
        <Text color="gray.600">
          {t('createReview.steps.selectAssessmentScopeSubtitle', {
            unitSingular: assessmentTerminology.singular,
            unitPlural: assessmentTerminology.plural,
          })}
        </Text>
      </Box>

      <Tabs.Root variant="enclosed" defaultValue="templates">
        <Tabs.List>
          <Tabs.Trigger value="templates">{t('createReview.steps.predefinedTemplates')}</Tabs.Trigger>
          <Tabs.Trigger value="custom">{t('createReview.steps.customSections', { unitPlural: assessmentTerminology.plural })}</Tabs.Trigger>
        </Tabs.List>

          {/* Predefined Templates Tab */}
          <Tabs.Content value="templates">
            <VStack gap={6} align="stretch">
              {/* Template Selection */}
              <Box>
                <Text fontWeight="semibold" mb={3}>
                  {t('createReview.steps.chooseTemplate')}
                </Text>
                <VStack gap={3}>
                  {templates.length === 0 && (
                    <Text color="gray.500">{t('createReview.steps.noTemplatesAvailableForStandard')}</Text>
                  )}
                  {templates.map((template) => (
                    <Card.Root key={template.id}
                      variant={selectedTemplate?.id === template.id ? 'elevated' : 'outline'}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: 'brand.300',
                        boxShadow: 'md',
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <CardBody>
                        <HStack justify="space-between">
                          <VStack align="start" gap={1}>
                            <Text fontWeight="semibold">{template.name}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {template.description}
                            </Text>
                            <HStack gap={2}>
                              <Badge colorPalette="blue" variant="subtle" size="sm">
                                {template.sections.includes('all')
                                  ? t('createReview.steps.allSections', { unitPlural: assessmentTerminology.plural })
                                  : t('createReview.steps.sectionsCount', {
                                      count: template.sections.length,
                                      unit: getAssessmentUnitLabels(selectedStandard?.standard_type, template.sections, i18n.language).plural,
                                    })}
                              </Badge>
                              {template.is_organization_template && (
                                <Badge colorPalette="green" variant="subtle" size="sm">
                                  {t('createReview.steps.organizationTemplate')}
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                          {selectedTemplate?.id === template.id && (
                            <Icon as={FiCheck} w={5} h={5} color="green.500" />
                          )}
                        </HStack>
                      </CardBody>
                    </Card.Root>
                  ))}
                </VStack>
              </Box>

              {/* Section Selection */}
              {sections.length > 0 && (
                <Box>
                  <Separator my={4} />
                  <HStack justify="space-between" mb={3}>
                    <VStack align="start" gap={1}>
                      <Text fontWeight="semibold">
                        {selectedRequirementCount > 0
                          ? t('createReview.steps.assessmentSectionsWithRequirements', {
                              selected: selectedSections.length,
                              requirements: selectedRequirementCount,
                              unitTitlePlural: assessmentTerminology.titlePlural,
                            })
                          : t('createReview.steps.assessmentSectionsSelected', {
                              selected: selectedSections.length,
                              unitTitlePlural: assessmentTerminology.titlePlural,
                            })}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {t('createReview.steps.assessmentSectionsHelper', {
                          unitSingular: assessmentTerminology.singular,
                          unitPlural: assessmentTerminology.plural,
                        })}
                      </Text>
                    </VStack>
                    <HStack gap={2}>
                      <Button
                        size="sm"
                        colorPalette="brand"
                        variant="outline"
                        onClick={handleSelectAllSections}
                      >
                        {t('createReview.steps.selectAll')}
                      </Button>
                      <Button
                        size="sm"
                        colorPalette="gray"
                        variant="outline"
                        onClick={handleDeselectAllSections}
                      >
                        {t('createReview.steps.deselectAll')}
                      </Button>
                    </HStack>
                  </HStack>
                  
                  <Box position="relative" mb={4}>
                    <Icon
                      as={FiSearch}
                      position="absolute"
                      left="12px"
                      top="50%"
                      transform="translateY(-50%)"
                      color="gray.400"
                      zIndex={1}
                      pointerEvents="none"
                    />
                    <Input
                      placeholder={t('createReview.steps.searchSectionsPlaceholder', {
                        unitPlural: assessmentTerminology.plural,
                      })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      bg="white"
                      pl="40px"
                    />
                  </Box>

                  <VStack gap={2} align="stretch" maxH="400px" overflowY="auto">
                    {filteredSections.map((section) => {
                      const subsections = parseSubsections(section);
                      const hasSubsections = subsections.length > 0;
                      const isExpanded = expandedSections.has(section.section_id);
                      const selectedSubsectionKeys = getSectionSelectionTokens(section.section_id);
                      const totalLeafMeasures = countTotalLeafMeasuresForSection(section);
                      const selectedLeafMeasures = countSelectedLeafMeasuresForSection(section.section_id, section);
                      const allSubsSelected = hasSubsections && subsections.length > 0 && subsections.every((sub: any) => isSubsectionFullySelected(section.section_id, sub));
                      const someSubsSelected = hasSubsections && selectedSubsectionKeys.length > 0 && !allSubsSelected;
                      // For sections with subsections: checked = all selected, indeterminate = some selected
                      // For sections without subsections: checked = section is in selectedSections
                      const isChecked = hasSubsections ? allSubsSelected : selectedSections.includes(section.section_id);

                      return (
                        <Card.Root key={section.section_id} variant="outline" size="sm">
                          <CardBody py={3}>
                            <VStack align="stretch" gap={2}>
                              {/* Main Section */}
                              <HStack gap={3} align="start">
                                <Checkbox.Root
                                  checked={someSubsSelected ? 'indeterminate' : isChecked}
                                  onCheckedChange={() => handleSectionToggle(section.section_id)}
                                  colorPalette="brand"
                                >
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control />
                                </Checkbox.Root>
                                <VStack align="start" gap={1} flex={1}>
                                  <HStack justify="space-between" w="full">
                                    <Text fontWeight="bold" fontSize="md">
                                      {formatSectionHeading(section)} - {section.section_title}
                                    </Text>
                                    {hasSubsections && (
                                      <IconButton
                                        aria-label={isExpanded ? t('createReview.steps.collapseSubsections') : t('createReview.steps.expandSubsections')}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => toggleSectionExpansion(section.section_id)}
                                      >
                                        <Icon as={isExpanded ? FiChevronDown : FiChevronRight} />
                                      </IconButton>
                                    )}
                                  </HStack>
                                  {!hasSubsections && (
                                    <Text fontSize="sm" color="gray.700">
                                      {section.section_content}
                                    </Text>
                                  )}
                                  {hasSubsections && (
                                    <Text fontSize="xs" color="gray.500">
                                      {t('createReview.steps.requirementsSelectedClickToExpand', { selected: selectedLeafMeasures, total: totalLeafMeasures })}
                                    </Text>
                                  )}
                                </VStack>
                              </HStack>

                              {/* Subsections */}
                              {hasSubsections && (
                                <Collapsible.Root open={isExpanded}>
                                  <Collapsible.Content>
                                    <Box pl={8} pt={2}>
                                      <VStack align="stretch" gap={2}>
                                        {subsections.map((subsection: any) => {
                                          const hasSubSubs = subsection.sub_subsections && Array.isArray(subsection.sub_subsections) && subsection.sub_subsections.length > 0;
                                          const measureIds = getLeafMeasureIds(subsection);
                                          const subsectionToken = subsection.id;
                                          const subsectionSelected = selectedSubsectionKeys.includes(subsectionToken);
                                          const selectedMeasureTokens = hasSubSubs
                                            ? measureIds.map((measureId: string) => getMeasureToken(subsection.id, measureId)).filter((token: string) => selectedSubsectionKeys.includes(token))
                                            : [];
                                          const isSubsectionSelected = subsectionSelected || (hasSubSubs && selectedMeasureTokens.length === measureIds.length && measureIds.length > 0);
                                          const isSubsectionIndeterminate = !subsectionSelected && hasSubSubs && selectedMeasureTokens.length > 0 && selectedMeasureTokens.length < measureIds.length;
                                          const isSubExpanded = expandedSubsections.has(subsection.id);
                                          // Full requirement text, with any leading "<id>:" prefix removed so it
                                          // doesn't repeat the ID already shown in the header above.
                                          const rawContent: string = subsection.content || '';
                                          const idPrefix = `${subsection.id}:`;
                                          const displayContent = rawContent.startsWith(idPrefix)
                                            ? rawContent.slice(idPrefix.length).trim()
                                            : rawContent;
                                          
                                          return (
                                            <Box
                                              key={subsection.id}
                                              p={2}
                                              borderRadius="md"
                                              bg={isSubsectionSelected ? "brand.50" : "gray.50"}
                                              borderWidth="1px"
                                              borderColor={isSubsectionSelected ? "brand.200" : "gray.200"}
                                            >
                                              <HStack gap={2} align="start">
                                                <Checkbox.Root
                                                  checked={isSubsectionIndeterminate ? 'indeterminate' : isSubsectionSelected}
                                                  onCheckedChange={() => handleSubsectionToggle(section.section_id, subsection.id, subsection.title)}
                                                  colorPalette="brand"
                                                  size="sm"
                                                >
                                                  <Checkbox.HiddenInput />
                                                  <Checkbox.Control />
                                                </Checkbox.Root>
                                                <VStack align="start" gap={1} flex={1}>
                                                  <HStack justify="space-between" w="full">
                                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                      {subsection.id} - {subsection.title}
                                                    </Text>
                                                    {hasSubSubs && (
                                                      <IconButton
                                                        aria-label={isSubExpanded ? t('createReview.steps.collapseRequirements') : t('createReview.steps.expandRequirements')}
                                                        size="xs"
                                                        variant="ghost"
                                                        onClick={() => toggleSubsectionExpansion(subsection.id)}
                                                      >
                                                        <Icon as={isSubExpanded ? FiChevronDown : FiChevronRight} />
                                                      </IconButton>
                                                    )}
                                                  </HStack>
                                                  {displayContent && displayContent !== subsection.title && (
                                                    <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap">
                                                      {displayContent}
                                                    </Text>
                                                  )}
                                                  {hasSubSubs && (
                                                    <Text fontSize="xs" color="gray.500">
                                                      {t('createReview.steps.requirementsSelectedClickToExpand', {
                                                        selected: countSelectedLeafMeasuresForSubsection(section.section_id, subsection),
                                                        total: measureIds.length,
                                                      })}
                                                    </Text>
                                                  )}
                                                  {hasSubSubs && !isSubExpanded && (
                                                    <Text fontSize="xs" color="gray.500">
                                                      {t('createReview.steps.requirementsClickToExpand', { count: subsection.sub_subsections.length })}
                                                    </Text>
                                                  )}
                                                </VStack>
                                              </HStack>

                                              {/* Sub-subsections (3rd level) */}
                                              {hasSubSubs && (
                                                <Collapsible.Root open={isSubExpanded}>
                                                  <Collapsible.Content>
                                                    <Box pl={8} pt={2}>
                                                      <VStack align="stretch" gap={1}>
                                                        {subsection.sub_subsections.map((subSub: any) => (
                                                          <HStack key={subSub.id} gap={2} p={1.5} borderRadius="sm" bg="white" borderWidth="1px" borderColor="gray.100" align="start">
                                                            <Checkbox.Root
                                                              checked={selectedSubsectionKeys.includes(getMeasureToken(subsection.id, subSub.id)) || subsectionSelected}
                                                              onCheckedChange={() => handleMeasureToggle(
                                                                section.section_id,
                                                                subsection.id,
                                                                subSub.id,
                                                                measureIds
                                                              )}
                                                              colorPalette="brand"
                                                              size="sm"
                                                            >
                                                              <Checkbox.HiddenInput />
                                                              <Checkbox.Control />
                                                            </Checkbox.Root>
                                                            <VStack align="start" gap={0}>
                                                              <Text fontSize="xs" fontWeight="medium" color="brand.600">
                                                                {subSub.id}
                                                              </Text>
                                                              <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
                                                                {subSub.content || subSub.title}
                                                              </Text>
                                                            </VStack>
                                                          </HStack>
                                                        ))}
                                                      </VStack>
                                                    </Box>
                                                  </Collapsible.Content>
                                                </Collapsible.Root>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </VStack>
                                    </Box>
                                  </Collapsible.Content>
                                </Collapsible.Root>
                              )}
                            </VStack>
                          </CardBody>
                        </Card.Root>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Tabs.Content>

          {/* Custom Sections Tab */}
          <Tabs.Content value="custom">
            <VStack gap={6} align="stretch">
              {/* Add Custom Section */}
              <Box>
                <Text fontWeight="semibold" mb={3}>
                  {t('createReview.steps.addCustomSection')}
                </Text>
                <VStack gap={3}>
                  <Textarea
                    placeholder={t('createReview.steps.customSectionPlaceholder', {
                      unitSingular: assessmentTerminology.singular,
                    })}
                    value={newCustomSection}
                    onChange={(e) => setNewCustomSection(e.target.value)}
                    rows={3}
                  />
                  <Button
                    colorPalette="brand"
                    onClick={handleAddCustomSection}
                    disabled={!newCustomSection.trim()}
                    alignSelf="start"
                  >
                    <Icon as={FiPlus} mr={2} />
                    {t('createReview.steps.addCustomSection')}
                  </Button>
                </VStack>
              </Box>

              {/* Custom Sections List */}
              {customSections.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    {t('createReview.steps.customSectionsCount', {
                      count: customSections.length,
                      unitPlural: assessmentTerminology.plural,
                    })}
                  </Text>
                  <VStack gap={2} align="stretch">
                    {customSections.map((section, index) => (
                      <Card.Root key={index} variant="outline">
                        <CardBody>
                          <HStack justify="space-between">
                            <Text>{section}</Text>
                            <Button
                              variant="ghost"
                              colorPalette="red"
                              size="sm"
                              onClick={() => handleRemoveCustomSection(index)}
                            >
                              <Icon as={FiX} mr={2} />
                              {t('createReview.steps.remove')}
                            </Button>
                          </HStack>
                        </CardBody>
                      </Card.Root>
                    ))}
                  </VStack>
                </Box>
              )}

              <Alert.Root status="info" borderRadius="md">
                <Alert.Indicator>
                  <Icon as={FiInfo} />
                </Alert.Indicator>
                <Box>
                  <Alert.Title>{t('createReview.steps.customSections', { unitPlural: assessmentTerminology.plural })}</Alert.Title>
                  <Alert.Description>
                    {t('createReview.steps.customSectionsDescription', { unitPlural: assessmentTerminology.plural })}
                  </Alert.Description>
                </Box>
              </Alert.Root>
            </VStack>
          </Tabs.Content>
      </Tabs.Root>

      {/* Summary */}
      {(selectedSections.length > 0 || customSections.length > 0) && (
        <Alert.Root status="success" borderRadius="md">
          <Alert.Indicator />
          <Box>
            <Alert.Title>{t('createReview.steps.assessmentConfiguration')}</Alert.Title>
            <Alert.Description>
              {selectedSections.length > 0 &&
                t('createReview.steps.standardSectionsSummary', {
                  count: selectedSections.length,
                  unitTitlePlural: assessmentTerminology.titlePlural,
                })}
              {selectedSections.length > 0 && customSections.length > 0 && t('createReview.steps.summarySeparator')}
              {customSections.length > 0 &&
                t('createReview.steps.customSectionsSummary', {
                  count: customSections.length,
                  unitPlural: assessmentTerminology.plural,
                })}
            </Alert.Description>
          </Box>
        </Alert.Root>
      )}
    </VStack>
  );
};

export default TemplateSelectionStep; 
